/** @jsx jsx */

/** @jsxFrag React.Fragment */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import type { Theme } from '@coursera/cds-core';
import { Tab, TabContext, TabPanel, Tabs, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockSkillsetsQuery,
  EntWebsiteSkillsetsFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import Image from 'bundles/ent-website/contentful/core/Image';
import { entWebsiteBlockSkillsetsQuery } from 'bundles/ent-website/queries/EntWebsiteSkillsets';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteSkillsetsFragment>;

export function SkillsetsModule({ content, assembly }: Props) {
  const { eyebrow } = assembly;
  const { headline, description, skillsetsCollection } = content;

  const [currentTab, setCurrentTab] = useState<string>('0');

  const styles: Record<string, Interpolation<Theme>> = {
    entryTitle: () => css`
      text-transform: uppercase;
      margin-bottom: var(--cds-spacing-300);
    `,
    headline: () => css`
      line-height: var(--cds-spacing-600);
      margin-bottom: var(--cds-spacing-300);
    `,
    description: css`
      max-width: 1100px;
      font-size: 20px;
    `,
    tabContent: () => css`
      margin-top: 64px;
      padding-right: 75px;
      border-right: 1px solid var(--cds-color-grey-50);
      padding-left: 72px;

      ${breakpoints.down('xs')} {
        padding-left: 0;
        border-right: 0;
      }
    `,
    tabPanel: () => css`
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      ${breakpoints.down('xs')} {
        grid-template-columns: repeat(1, 1fr);
      }

      div:first-child {
        padding-left: 0;
      }

      div:last-child {
        border-right: none;
        padding-right: 0;
        ${breakpoints.down('xs')} {
          padding-left: 0;
        }
      }
    `,
    difficulty: () => css`
      margin-bottom: var(--cds-spacing-50);
    `,
    skillTitle: () => css`
      margin-bottom: var(--cds-spacing-300);
    `,
    sectionTitle: () => css`
      margin-bottom: var(--cds-spacing-200);
    `,
    skillSeparator: () => css`
      margin: 0 6px;
    `,
    skillsetWrapper: () => css`
      max-width: 350px;
    `,
    skillsAcquiredWrapper: () => css`
      list-style-type: none;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      padding-left: 0;
      padding-right: 0;
      margin-bottom: var(--cds-spacing-400);

      li:not(:last-child)::after {
        content: '\u00b7';
        padding: 0 var(--cds-spacing-100);
      }
    `,
    skillsGained: () => css`
      margin-bottom: var(--cds-spacing-600);
      list-style-type: none;
      padding-inline-start: 0;
      width: 100%;
      display: flex;
      flex-wrap: wrap;

      li {
        display: flex;
        width: 100%;
        flex-shrink: 0;
        flex-grow: 1;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        font-size: 16px !important;
        line-height: 150% !important;
        letter-spacing: -0.03em;
        font-weight: 400;
        flex-wrap: nowrap;
        margin-bottom: var(--cds-spacing-150);

        ${breakpoints.down('xs')} {
          font-size: 14px !important;
          line-height: 140% !important;
          letter-spacing: -0.02em;
        }

        & > span:nth-child(1) {
          margin-right: var(--cds-spacing-100);
          color: var(--cds-color-blue-700);
        }

        & > span:nth-child(2) {
          flex-grow: 1;
        }
      }
    `,
    partnerList: () => css`
      list-style-type: none;
      padding-inline-start: 0;
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(5, 1fr);
      flex-wrap: wrap;

      li {
        height: 48px;
        width: 48px;
        background: white;
        border: 1px solid var(--cds-color-grey-50);
        border-radius: 100%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          height: 40px;
          max-width: 40px;
          object-fit: contain;
        }
      }
    `,
  };

  const filterTabContent = (tabName: string) => {
    return skillsetsCollection?.items?.filter((skillset) => {
      return skillset?.tabName === tabName;
    });
  };

  const contentPanel = () => {
    return (
      <div>
        <Typography variant="h2semibold" css={styles.entryTitle}>
          {eyebrow}
        </Typography>
        <Typography variant="d2semibold" css={styles.headline}>
          {headline}
        </Typography>
        <Typography2
          component="p"
          variant="bodyPrimary"
          css={[
            styles.description,
            content.tabNames && content.tabNames?.length > 1 ? { marginBottom: 80 } : { marginBottom: 0 },
          ]}
        >
          {description}
        </Typography2>
        <TabContext value={String(currentTab)}>
          {content.tabNames && content.tabNames.length > 1 && (
            <>
              {/* @ts-expect-error */}
              <Tabs
                aria-label="Content tabs"
                variant="section"
                keyboardActivation="automatic"
                onChange={(value: string) => setCurrentTab(value)}
              >
                {content.tabNames?.map((tab, index) => tab && <Tab label={tab} value={String(index)} key={tab} />)}
              </Tabs>
            </>
          )}

          {content.tabNames?.map(
            (tab, idx) =>
              tab && (
                <TabPanel value={String(idx)} css={styles.tabPanel} key={tab}>
                  {filterTabContent(tab)?.map((skillset) => (
                    <div css={styles.tabContent}>
                      <div css={styles.skillsetWrapper}>
                        <Typography variant="h2semibold" css={styles.skillTitle}>
                          {skillset?.skillTitle}
                        </Typography>

                        {skillset?.skillsAcquiredTitle && (
                          <Typography2 component="h3" variant="subtitleMedium" css={styles.sectionTitle}>
                            {skillset.skillsAcquiredTitle}
                          </Typography2>
                        )}

                        <ul css={styles.skillsAcquiredWrapper}>
                          {skillset?.skillsAquired?.map((skill) => (
                            <li>
                              <Typography2 variant="bodySecondary" component="span" key={skill}>
                                {skill}
                              </Typography2>
                            </li>
                          ))}
                        </ul>

                        {skillset?.skillsGainedChecklistTitle && (
                          <Typography2 component="h3" variant="subtitleMedium" css={styles.sectionTitle}>
                            {skillset.skillsGainedChecklistTitle}
                          </Typography2>
                        )}
                        <ul css={styles.skillsGained}>
                          {skillset?.skillsGainedChecklist?.map((skill) => (
                            <li key={skill}>
                              <span>
                                <CheckIcon size="large" />
                              </span>
                              <Typography2 variant="bodySecondary" component="span">
                                {skill}
                              </Typography2>
                            </li>
                          ))}
                        </ul>
                        {skillset?.partnersTitle && (
                          <Typography2 component="h3" variant="subtitleMedium" css={styles.sectionTitle}>
                            {skillset.partnersTitle}
                          </Typography2>
                        )}
                        <ul css={styles.partnerList}>
                          {skillset?.partnersCollection?.items.map(
                            (partner) =>
                              partner?.logo && (
                                <li key={partner.slug}>
                                  <Image content={partner.logo} width="auto" />
                                </li>
                              )
                          )}
                        </ul>
                      </div>
                    </div>
                  ))}
                </TabPanel>
              )
          )}
        </TabContext>
      </div>
    );
  };

  return <BlockLayout assembly={assembly} renderContent={contentPanel} alignItems="flex-start" />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockSkillsetsModule = withBlockQuery<EntWebsiteSkillsetsFragment, EntWebsiteBlockSkillsetsQuery>(
  entWebsiteBlockSkillsetsQuery
)(SkillsetsModule);
